import type { ProjectBrief } from "../contracts/types";
import { DEFAULT_SPECIAL_CONDITIONS } from "../contracts/specialConditions";

/** A clean, empty contract to start a new project — structural defaults kept,
 * all content fields blank so you go straight into the editor. */
export function blankProject(): ProjectBrief {
  return {
    standard: "FIDIC_REDBOOK_2017",
    projectName: "",
    projectLocation: "",
    contractReference: "",
    worksDescription: "",
    employer: { name: "", address: "", representative: "", representativeTitle: "" },
    contractor: { name: "", address: "", representative: "", representativeTitle: "" },
    engineer: { name: "", address: "" },
    contractPrice: { amount: NaN, currency: "USD" },
    timeForCompletion: "",
    commencementDate: "",
    defectsNotification: "12 months",
    delayDamagesPerDay: { amount: NaN, currency: "USD" },
    delayDamagesCapPercent: 10,
    performanceSecurityPercent: 10,
    advancePaymentPercent: 0,
    retentionPercent: 5,
    retentionCapPercent: 5,
    minimumIpc: { amount: NaN, currency: "USD" },
    paymentPeriodDays: 56,
    overheadProfitPercent: 15,
    governingLaw: "",
    rulingLanguage: "English",
    communicationsLanguage: "English",
    disputeResolution: "",
    daab: { kind: "DAAB", members: 1, appointmentWeeks: 4 },
    addenda: [],
    narrative: [],
    specialProvisions: [],
    drawings: [],
    specifications: [],
    agreementDate: "",
    coverDate: "",
    preparedBy: { name: "", org: "", role: "", address: "" },
  };
}

/** The studio's own details — we are the Architect on a design-services
 * appointment, so a new architecture contract pre-fills our side. */
const STUDIO = {
  name: "HD Studio Inc.",
  address: "21 Princess Elizabeth Rd, New Amsterdam, Berbice, Guyana",
  representative: "R. Ferdinand",
  representativeTitle: "Principal Architect",
};

/** A blank Owner–Architect (design services) agreement between the studio and a
 * client: we are pre-filled as the Architect, the Owner/Client is left blank.
 * Construction-only mechanics (retention, delay damages, DAAB, advance) are
 * cleared — they don't apply to a design appointment. */
export function blankArchitectureProject(): ProjectBrief {
  const b = blankProject();
  return {
    ...b,
    standard: "AIA_B101_2017",
    // Owner / Client — to be filled in.
    employer: { name: "", address: "", representative: "", representativeTitle: "" },
    // Architect — that's us.
    contractor: { ...STUDIO },
    engineer: { name: "", address: "" },
    // Design fee: percentage of the Cost of the Work is the usual basis.
    designFeeBasis: "percentage",
    estimatedConstructionCost: { amount: NaN, currency: "USD" },
    architectServices: ["SD", "DD", "CD", "BN", "CA"],
    reimbursablesNote: "at cost plus 10%",
    paymentPeriodDays: 30,
    // Clear construction-contract mechanics that have no place in an appointment.
    defectsNotification: "",
    delayDamagesPerDay: undefined,
    delayDamagesCapPercent: undefined,
    performanceSecurityPercent: undefined,
    advancePaymentPercent: undefined,
    retentionPercent: undefined,
    retentionCapPercent: undefined,
    minimumIpc: undefined,
    overheadProfitPercent: undefined,
    daab: undefined,
    preparedBy: { name: STUDIO.representative, org: STUDIO.name, role: STUDIO.representativeTitle, address: STUDIO.address },
  };
}

// Seeded from the real TRICAN / Potaro Gardens contract documents (Part A
// Contract Data, BOQ Particulars B, Forms of Contract). Figures are the actual
// tendered values; swap as needed before execution.
export const SAMPLE_PROJECT: ProjectBrief = {
  standard: "FIDIC_REDBOOK_2017",

  projectName: "Potaro Gardens by Trican Developments Inc.",
  projectLocation: "Georgetown, Guyana",
  contractReference: "2025.DH.PG.MB.01",
  worksDescription:
    "Construction of Potaro Gardens — a 10-storey condominium building featuring 103 private apartment units, a community pool, external works and all mechanical, electrical and plumbing (MEP) works, civil construction and finishes, in accordance with the FIDIC Red Book and IBC 2021",

  employer: {
    name: "Potaro Gardens Inc. (Trican Developments Inc.)",
    address: "79 Premniranjan Place, Prashad Nagar, Georgetown, Guyana",
    representative: "Andrew Pollard",
    representativeTitle: "Company Secretary",
    email: "info@potaroguyana.com",
    witnessName: "Abigail Welch",
    witnessAddress: "",
  },
  contractor: {
    name: "Qingjian Group Company (Guyana) Inc.",
    address: "Construction Site, Marriott Hotel, Cheddi Jagan International Airport Roundabout, Timehri, EBD, Guyana",
    representative: "Liu Hanjin",
    representativeTitle: "Director",
    phone: "+592 677 6619",
    witnessName: "Renauto Ferdinand",
    witnessAddress: "26 Main and Harkmans Lanes, New Amsterdam, Berbice",
  },
  engineer: {
    name: "HD Studio Inc. (Supervisory Consultant / Engineer)",
    address: "Guyana",
    email: "renauto.ferdinand@hddesignstudiogy.com",
  },

  contractPrice: { amount: 46830344, currency: "USD" },
  // Contract sum in words is derived from the figure, not stored (avoids drift).

  timeForCompletion: "30 Months",
  commencementDate: "",
  defectsNotification: "12 months",

  delayDamagesPerDay: { amount: 1500, currency: "USD" },
  delayDamagesCapPercent: 10,
  performanceSecurityPercent: 10,
  advancePaymentPercent: 15,
  advanceFixedStart: { amount: 700000, currency: "GYD" },
  advanceInstallments: 3,
  advanceRepayment: "4 installments starting from Valuation 2",
  retentionPercent: 10,
  retentionCapPercent: 10,
  retentionRelease: "5% upon Practical Completion, 5% upon exit of the Defects Notification Period",
  minimumIpc: { amount: 45000, currency: "USD" },
  paymentPeriodDays: 42,
  overheadProfitPercent: 15,

  governingLaw: "Guyana",
  rulingLanguage: "English",
  communicationsLanguage: "English",
  disputeResolution:
    "Disputes of a professional or technical nature shall first be referred to a Dispute Resolution Board (DRB) under GAPE; if the DRB ruling is unacceptable to either Party, the dispute may be escalated to arbitration under Clause 21",
  daab: { kind: "DRB", members: 3, appointmentWeeks: 4, registrationBody: "Guyana Association of Professional Engineers (GAPE)" },

  addenda: ["1", "2", "3"],

  narrative: [
    {
      marker: "A",
      heading: "The Project",
      text: "Potaro Gardens by Trican Developments Inc., designed by HD Studio Inc., represents a pioneering venture in Guyana's real estate landscape — the country's first culturally focused luxury condominium project. This 10-storey development comprises 103 luxury units delivering a high standard of construction and finishing to IBC 2021 building codes, managed under FIDIC and AIA guidelines.",
    },
    {
      marker: "B",
      heading: "Name of Parties",
      text: "Client / Employer: Trican Developments Inc., Lots 187, 189, 191 Cumminglodge, Demerara-Mahaica (Representative: A. Welch). Architect, Supervisory Consultant, M+E Consultant and Quantity Surveyor: HD Studio Inc.",
    },
    {
      marker: "C",
      heading: "Site Access",
      text: "Site access for the purpose of the works and tendering shall be by prior appointment with the project consultant.",
    },
    {
      marker: "D",
      heading: "Existing Site Conditions",
      text: "At the time of tender, access to the site is limited to a dirt road. There is no existing power supply or water service on site, necessitating temporary solutions until utilities become available. The area is densely covered with high foliage requiring clearance, and the property is not fenced — securing the site is among the initial measures to be undertaken. Bidders should plan for initial independence in terms of power and water supply.",
    },
    {
      marker: "E",
      heading: "Site Usage Limitations",
      text: "The Contractor shall protect all existing infrastructure and services, minimise disruption, maintain access for emergency services and local residents, adhere to environmental regulations (erosion control, stormwater management, prevention of soil and water contamination), comply with all local and regulatory permit requirements, and coordinate closely with local authorities, utility providers and other stakeholders.",
    },
    {
      marker: "F",
      heading: "Description of the Works",
      text: "Comprehensive construction of the 10-storey, 103-unit condominium: reinforced concrete pile foundation and concrete substructure; structural steel superstructure erected by tower crane or approved equivalent; external walls of curtain wall / shopfront units, lightweight steel frame and hollow concrete blocks; gypsum drywall internal partitions; full electrical and mechanical systems; community pool; landscaping and external works. Security systems are excluded from the scope at tender stage.",
    },
    {
      marker: "G",
      heading: "Project Terms",
      text: "The commercial terms of the Contract — the Contract Price, Defects Notification Period, advance payment and its repayment, delay (liquidated) damages and their cap, retention and its release — are as stated in the Particular Conditions Part A (Contract Data) and shall govern. No figures are restated here to avoid conflict with the Contract Data.",
    },
  ],

  specialProvisions: [
    { clauseRef: "1.3", heading: "Electronic communications platform", kind: "amend", text: "All submissions, communications, drawings and progress reports shall be made through the Procore electronic platform. Paper copies are not required unless specifically requested by the Engineer." },
    { clauseRef: "6.5", heading: "Normal working hours", kind: "amend", text: "Normal working hours on the Site shall be 7:00am to 5:00pm, varying as required and subject to the Engineer's approval for out-of-hours work." },
    { clauseRef: "13.5", heading: "Value Added Tax", kind: "add", text: "The Contractor shall submit copies of its VAT Registration Certificate and a valid VAT Clearance Certificate. All certified amounts are exclusive of VAT; VAT shall be added in accordance with the prevailing tax legislation at the time of invoicing." },
  ],

  drawings: [
    { number: "A-000", title: "Architectural Drawing Package (under separate cover)", revision: "—", discipline: "Architectural" },
    { number: "S-000", title: "Civil / Structural Engineering Package", revision: "—", discipline: "Structural" },
    { number: "E-000", title: "Electrical Package", revision: "—", discipline: "Electrical" },
    { number: "M-000", title: "Mechanical / Air-Conditioning Package", revision: "—", discipline: "Mechanical" },
    { number: "P-000", title: "Plumbing / Sanitary Package", revision: "—", discipline: "Plumbing" },
  ],

  specifications: [
    { title: "Bill of Quantities (Part 1 — Bidding, BOQ, Technical Proposal Forms)", reference: "BOQ" },
    { title: "Works Requirements (Part 2)", reference: "WR" },
    { title: "Conditions of Contract & Contract Forms (Part 3)", reference: "COC" },
  ],

  agreementDate: "",
  coverDate: "19 April 2025",

  preparedBy: {
    name: "R. Ferdinand",
    org: "HD Studio Inc.",
    role: "Architect / Structural Engineer / MEP / Landscape Architect",
    address: "21 Princess Elizabeth Rd, New Amsterdam, Berbice",
  },

  specialConditionsTitle: "Special Conditions of Contract",
  specialConditionsDate: "18 April 2025",
  specialConditionsBlocks: DEFAULT_SPECIAL_CONDITIONS,
};
